"use client";

import {
  LandingBackgroundImagePuckField,
  LandingPdfUploadPuckField,
} from "@/components/admin/landings/landing-background-image-field";
import { LandingBackgroundColorField } from "@/components/admin/landings/landing-background-color-field";
import { LandingStyledTextField } from "@/components/admin/landings/landing-styled-text-field";
import { LandingIconPickerField } from "@/components/admin/landings/landing-icon-picker-field";
import { LANDING_ICON_OPTIONS } from "@/lib/landing/puck/icon-options";
import type { LandingTextValue } from "@/lib/landing/puck/styled-text";
import { styledTextString } from "@/lib/landing/puck/styled-text";

export const landingSectionIdField = {
  type: "text" as const,
  label: "Якорь (id секции)",
};

export function landingStyledTextField(label: string, multiline = false) {
  return {
    type: "custom" as const,
    label,
    render: ({
      value,
      onChange,
    }: {
      value: LandingTextValue;
      onChange: (value: LandingTextValue) => void;
    }) => (
      <LandingStyledTextField
        value={value}
        onChange={onChange}
        label={label}
        multiline={multiline}
      />
    ),
  };
}

export const landingSectionTitleField = landingStyledTextField("Заголовок");
export const landingSectionSubtitleField = landingStyledTextField(
  "Подзаголовок",
  true,
);

export function landingBackgroundColorFieldLabeled(label: string) {
  return {
    type: "custom" as const,
    label,
    render: ({
      value,
      onChange,
    }: {
      value: string;
      onChange: (value: string) => void;
    }) => (
      <LandingBackgroundColorField
        value={typeof value === "string" ? value : ""}
        onChange={onChange}
        label={label}
      />
    ),
  };
}

export const landingBackgroundColorField =
  landingBackgroundColorFieldLabeled("Цвет фона блока");

export const landingBackgroundColorDarkField =
  landingBackgroundColorFieldLabeled("Цвет фона (тёмная тема)");

/** Единое S3-поле картинки (фон / контент) — без ручного URL. */
export function landingImageUploadField(label: string, folder?: string) {
  return {
    type: "custom" as const,
    label,
    render: ({
      value,
      onChange,
    }: {
      value: string;
      onChange: (value: string) => void;
    }) => (
      <LandingBackgroundImagePuckField
        value={typeof value === "string" ? value : ""}
        onChange={onChange}
        label={label}
        folder={folder}
      />
    ),
  };
}

export function landingPdfUploadField(label: string, folder = "cases") {
  return {
    type: "custom" as const,
    label,
    render: ({
      value,
      onChange,
    }: {
      value: string;
      onChange: (value: string) => void;
    }) => (
      <LandingPdfUploadPuckField
        value={typeof value === "string" ? value : ""}
        onChange={onChange}
        label={label}
        folder={folder}
      />
    ),
  };
}

export const landingBackgroundImageField = landingImageUploadField(
  "Фоновое изображение",
);

/** Главная / контентная картинка блока — тот же S3 upload. */
export const landingContentImageField =
  landingImageUploadField("Главная картинка");

/** Скриншот в MacBook-мокапе — светлая / тёмная тема. */
export const landingMockupLightImageField = landingImageUploadField(
  "Картинка мокапа (светлая тема)",
);
export const landingMockupDarkImageField = landingImageUploadField(
  "Картинка мокапа (тёмная тема)",
);

/** Фон Hero — светлая / тёмная тема. */
export const landingHeroBgLightImageField = landingImageUploadField(
  "Фон Hero (светлая тема)",
);
export const landingHeroBgDarkImageField = landingImageUploadField(
  "Фон Hero (тёмная тема)",
);

/** Фон CTA — светлая / тёмная тема. */
export const landingCtaBgLightImageField = landingImageUploadField(
  "Фон CTA (светлая тема)",
);
export const landingCtaBgDarkImageField = landingImageUploadField(
  "Фон CTA (тёмная тема)",
);

/** Картинка промо-баннера — светлая / тёмная тема. */
export const landingPromoBannerLightImageField = landingImageUploadField(
  "Картинка баннера (светлая тема)",
  "banners",
);
export const landingPromoBannerDarkImageField = landingImageUploadField(
  "Картинка баннера (тёмная тема)",
  "banners",
);

/** HeroSplit — картинка справа (сотрудник загружает в S3). */
export const landingHeroSplitLightImageField = landingImageUploadField(
  "Картинка справа (светлая тема)",
);
export const landingHeroSplitDarkImageField = landingImageUploadField(
  "Картинка справа (тёмная тема)",
);

export const landingIconSelectField = {
  type: "select" as const,
  label: "Иконка",
  options: [...LANDING_ICON_OPTIONS],
};

export const landingIconPickerField = {
  type: "custom" as const,
  label: "Иконка",
  render: ({
    value,
    onChange,
  }: {
    value: string;
    onChange: (value: string) => void;
  }) => (
    <LandingIconPickerField
      value={typeof value === "string" ? value : ""}
      onChange={onChange}
    />
  ),
};

export const landingCardArrayFields = {
  icon: landingIconPickerField,
  title: landingStyledTextField("Заголовок"),
  text: landingStyledTextField("Текст", true),
};

/** Сегменты «Для кого» — с опциональной CTA для режима табов / visual. */
export const landingAudienceArrayFields = {
  ...landingCardArrayFields,
  imageUrl: landingContentImageField,
  cardStyle: {
    type: "radio" as const,
    label: "Тип плитки (visual)",
    options: [
      { label: "Фото", value: "default" },
      { label: "CTA без фото", value: "cta" },
    ],
  },
  ctaLabel: landingStyledTextField("Кнопка / ссылка в тексте"),
  ctaHref: { type: "text" as const, label: "URL кнопки / ссылки" },
};

export const landingFeatureArrayFields = {
  icon: landingIconPickerField,
  title: landingStyledTextField("Заголовок"),
  description: landingStyledTextField("Описание", true),
  metric: landingStyledTextField("Метрика / бейдж (режим «чипы + карточки»)"),
  imageUrl: {
    ...landingContentImageField,
    label: "Скриншот (режим «табы + превью»)",
  },
};

/** Строки настраиваемой сетки RowGrid и template cards.rows. */
export const landingRowGridRowsField = {
  type: "array" as const,
  label: "Строки (кубики на всю ширину строки)",
  arrayFields: {
    columns: {
      type: "number" as const,
      label: "Колонок (0 = по числу кубиков)",
      min: 0,
      max: 6,
    },
    gap: {
      type: "radio" as const,
      label: "Отступ",
      options: [
        { label: "Малый", value: "small" },
        { label: "Средний", value: "medium" },
        { label: "Большой", value: "large" },
      ],
    },
    cards: {
      type: "array" as const,
      label: "Кубики в строке",
      arrayFields: {
        icon: landingIconPickerField,
        title: landingStyledTextField("Заголовок"),
        text: landingStyledTextField("Текст", true),
        imageUrl: landingContentImageField,
      },
      defaultItemProps: {
        icon: "search",
        title: "Пункт",
        text: "Описание",
        imageUrl: "",
      },
    },
  },
  defaultItemProps: ((_index: number) => ({
    columns: 0,
    gap: "medium",
    cards: [
      {
        icon: "search",
        title: "Пункт 1",
        text: "Описание",
        imageUrl: "",
      },
      {
        icon: "radar",
        title: "Пункт 2",
        text: "Описание",
        imageUrl: "",
      },
    ],
  })) as never,
};

export const landingComparisonRowArrayFields = {
  feature: { type: "text" as const, label: "Критерий (строка)" },
  ours: {
    type: "text" as const,
    label: "Мы: yes / no / текст",
  },
  other: {
    type: "text" as const,
    label: "Колонка 2: yes / no / текст",
  },
  third: {
    type: "text" as const,
    label: "Колонка 3: yes / no / текст",
  },
};

export const landingFaqArrayFields = {
  question: landingStyledTextField("Вопрос"),
  answer: landingStyledTextField("Ответ", true),
};

export const landingNewsItemArrayFields = {
  title: landingStyledTextField("Заголовок"),
  description: landingStyledTextField("Краткое описание", true),
  category: landingStyledTextField("Категория (для фильтра)"),
  date: landingStyledTextField("Дата (как показать)"),
  href: {
    type: "text" as const,
    label: "Ссылка на страницу новости",
  },
  coverUrl: landingImageUploadField("Обложка карточки", "news"),
};

/** Логотипы партнёров — сайт всегда в новой вкладке. */
export const landingPartnerItemArrayFields = {
  title: landingStyledTextField("Название партнёра"),
  href: {
    type: "text" as const,
    label: "Сайт партнёра (новая вкладка)",
  },
  logoUrl: landingImageUploadField("Логотип", "partners"),
};

export const landingCaseArrayFields = {
  title: landingStyledTextField("Название"),
  summary: landingStyledTextField("Кратко", true),
  coverUrl: landingImageUploadField("Обложка", "cases"),
  pdfUrl: landingPdfUploadField("PDF отчёта", "cases"),
};

export const landingFooterLinkArrayFields = {
  href: { type: "text" as const, label: "URL" },
  label: landingStyledTextField("Подпись"),
  group: {
    type: "select" as const,
    label: "Группа",
    options: [
      { label: "Продукт", value: "product" },
      { label: "Юридическая информация", value: "legal" },
    ],
  },
};

export const landingLinkListArrayFields = {
  kind: {
    type: "radio" as const,
    label: "Тип строки",
    options: [
      { label: "Ссылка", value: "link" },
      { label: "Текст", value: "text" },
    ],
  },
  label: landingStyledTextField("Текст"),
  href: { type: "text" as const, label: "URL (для ссылки)" },
  external: {
    type: "radio" as const,
    label: "Внешняя ссылка",
    options: [
      { label: "Нет", value: false },
      { label: "Да (новая вкладка)", value: true },
    ],
  },
  icon: landingIconPickerField,
};

export const landingFooterColumnItemArrayFields = {
  kind: {
    type: "radio" as const,
    label: "Тип",
    options: [
      { label: "Ссылка", value: "link" },
      { label: "Текст", value: "text" },
    ],
  },
  label: landingStyledTextField("Текст"),
  href: { type: "text" as const, label: "URL (для ссылки)" },
  icon: landingIconPickerField,
  muted: {
    type: "radio" as const,
    label: "Приглушённый (текст)",
    options: [
      { label: "Нет", value: "false" },
      { label: "Да", value: "true" },
    ],
  },
  external: {
    type: "radio" as const,
    label: "Внешняя ссылка",
    options: [
      { label: "Нет", value: "false" },
      { label: "Да", value: "true" },
    ],
  },
};

export const landingFooterColumnArrayFields = {
  id: { type: "text" as const, label: "ID колонки" },
  title: landingStyledTextField("Заголовок колонки"),
  items: {
    type: "array" as const,
    label: "Пункты",
    arrayFields: landingFooterColumnItemArrayFields,
    getItemSummary: (item: { label?: unknown }) =>
      styledTextString(item.label) || "Пункт",
    defaultItemProps: {
      kind: "link",
      href: "#",
      label: "Ссылка",
      icon: "link",
    },
  },
};

type PuckFieldLike = {
  type?: string;
  label?: string;
  landingUi?: string;
  multiline?: boolean;
  arrayFields?: Record<string, unknown>;
  [key: string]: unknown;
};

function enhanceFieldForEditor(field: unknown): unknown {
  if (!field || typeof field !== "object") return field;
  const f = field as PuckFieldLike;
  if (f.landingUi === "styled-text") {
    return landingStyledTextField(String(f.label ?? ""), Boolean(f.multiline));
  }
  if (f.type === "array" && f.arrayFields && typeof f.arrayFields === "object") {
    return {
      ...f,
      arrayFields: Object.fromEntries(
        Object.entries(f.arrayFields).map(([key, nested]) => [
          key,
          enhanceFieldForEditor(nested),
        ]),
      ),
    };
  }
  return field;
}

/** JSON-safe text/textarea → custom-поля типографики в редакторе. */
export function enhanceLandingConfigFieldsForEditor<
  T extends {
    components: Record<string, { fields?: Record<string, unknown> }>;
  },
>(config: T): T {
  const components = Object.fromEntries(
    Object.entries(config.components).map(([name, component]) => [
      name,
      {
        ...component,
        fields: Object.fromEntries(
          Object.entries(component.fields ?? {}).map(([key, field]) => [
            key,
            enhanceFieldForEditor(field),
          ]),
        ),
      },
    ]),
  ) as T["components"];
  return { ...config, components };
}
